import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export type PlatformSurvey = {
  id: string;
  title: string;
  description: string | null;
  survey_type: "impact" | "general";
  target_role: "entrepreneur" | "mentor" | "both";
  targeting_mode: "all" | "cohort" | "individual";
  status: "draft" | "active" | "closed";
  recurrence: "once" | "quarterly" | "annually";
  survey_group_id: string | null;
  created_by: string;
  created_at: string;
  published_at: string | null;
  updated_at: string;
};

export type PlatformSurveyQuestion = {
  id: string;
  survey_id: string;
  question_text: string;
  question_type: "multiple_choice" | "rating" | "text" | "number" | "currency";
  options: any | null;
  impact_metric_key: string | null;
  group_question_key: string | null;
  display_order: number;
  required: boolean;
};

export type SurveyWithCount = PlatformSurvey & { question_count: number };

const KEY = ["platform_surveys"];

export function usePlatformSurveys() {
  const query = useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<SurveyWithCount[]> => {
      const { data, error } = await supabase
        .from("platform_surveys")
        .select("*, platform_survey_questions(id)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []).map((s: any) => ({
        ...s,
        question_count: s.platform_survey_questions?.length || 0,
      }));
    },
  });
  return { surveys: query.data, isLoading: query.isLoading, error: query.error };
}

export function usePlatformSurvey(surveyId: string | null) {
  const query = useQuery({
    queryKey: ["platform_survey", surveyId],
    enabled: !!surveyId,
    queryFn: async () => {
      const [s, q] = await Promise.all([
        supabase.from("platform_surveys").select("*").eq("id", surveyId!).single(),
        supabase
          .from("platform_survey_questions")
          .select("*")
          .eq("survey_id", surveyId!)
          .order("display_order", { ascending: true }),
      ]);
      if (s.error) throw s.error;
      if (q.error) throw q.error;
      return { survey: s.data as PlatformSurvey, questions: (q.data || []) as PlatformSurveyQuestion[] };
    },
  });
  return {
    survey: query.data?.survey,
    questions: query.data?.questions,
    isLoading: query.isLoading,
    error: query.error,
  };
}

export function useCreatePlatformSurvey() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: Partial<PlatformSurvey>) => {
      if (!user?.id) throw new Error("Not authenticated");
      const { data, error } = await supabase
        .from("platform_surveys")
        .insert({
          title: input.title || "Untitled Survey",
          description: input.description || null,
          survey_type: (input.survey_type || "general") as any,
          target_role: (input.target_role || "both") as any,
          targeting_mode: (input.targeting_mode || "all") as any,
          status: (input.status || "draft") as any,
          recurrence: (input.recurrence || "once") as any,
          survey_group_id: input.survey_group_id || null,
          created_by: user.id,
        })
        .select()
        .single();
      if (error) throw error;
      return data as PlatformSurvey;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdatePlatformSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: Partial<PlatformSurvey> & { id: string }) => {
      const { data, error } = await supabase
        .from("platform_surveys")
        .update({
          title: patch.title,
          description: patch.description || null,
          survey_type: patch.survey_type as any,
          target_role: patch.target_role as any,
          targeting_mode: patch.targeting_mode as any,
          status: patch.status as any,
          recurrence: patch.recurrence as any,
        })
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as PlatformSurvey;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDuplicatePlatformSurvey() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (sourceId: string): Promise<PlatformSurvey> => {
      if (!user?.id) throw new Error("Not authenticated");
      const { data: src, error: srcErr } = await supabase
        .from("platform_surveys")
        .select("*")
        .eq("id", sourceId)
        .single();
      if (srcErr) throw srcErr;

      const groupId = src.survey_group_id || src.id;

      const { data: created, error: insErr } = await supabase
        .from("platform_surveys")
        .insert({
          title: `${src.title} (Copy)`,
          description: src.description,
          survey_type: src.survey_type,
          target_role: src.target_role,
          targeting_mode: src.targeting_mode,
          recurrence: src.recurrence,
          status: "draft",
          survey_group_id: groupId,
          created_by: user.id,
        })
        .select()
        .single();
      if (insErr) throw insErr;

      const { data: srcQs, error: qErr } = await supabase
        .from("platform_survey_questions")
        .select("*")
        .eq("survey_id", sourceId)
        .order("display_order", { ascending: true });
      if (qErr) throw qErr;

      if (srcQs && srcQs.length > 0) {
        const rows = srcQs.map((q) => ({
          survey_id: created.id,
          question_text: q.question_text,
          question_type: q.question_type,
          options: q.options,
          impact_metric_key: q.impact_metric_key,
          group_question_key: q.group_question_key,
          display_order: q.display_order,
          required: q.required,
        }));
        const { error: insQErr } = await supabase.from("platform_survey_questions").insert(rows);
        if (insQErr) throw insQErr;
      }

      return created as PlatformSurvey;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeletePlatformSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("platform_surveys").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Slug used as the default cross-wave grouping key for a question. */
function slugifyQuestionText(text: string): string {
  return (text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

/** Thrown when saving would cascade-delete submitted answers. */
export class SurveyAnswerLossError extends Error {
  constructor(
    public readonly answerCount: number,
    public readonly responseCount: number,
    public readonly deletedQuestionCount: number,
  ) {
    super(
      `Removing ${deletedQuestionCount} question(s) would permanently delete ${answerCount} submitted answer(s) across ${responseCount} response(s).`,
    );
    this.name = "SurveyAnswerLossError";
  }
}

export type UpsertQuestionInput = Omit<PlatformSurveyQuestion, "survey_id"> & {
  /** Present for existing rows; omitted/null for newly added questions. */
  id?: string | null;
};

/**
 * Saves the question set for a survey.
 *
 * This used to be delete-all-then-reinsert. Because
 * platform_survey_answers_question_id_fkey is ON DELETE CASCADE, that erased
 * every answer users had submitted — one click of Save destroyed the survey's
 * results while the responses rows survived, leaving completions with no answers.
 *
 * Now it is a real upsert: existing questions are matched by id and UPDATEd,
 * only genuinely new ones are INSERTed, and only questions the admin explicitly
 * removed are DELETEd. If those deletions would cascade away submitted answers,
 * it throws SurveyAnswerLossError unless the caller passes confirmAnswerLoss.
 */
export function useUpsertPlatformSurveyQuestions(surveyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      questions,
      confirmAnswerLoss = false,
    }: {
      questions: UpsertQuestionInput[];
      confirmAnswerLoss?: boolean;
    }) => {
      if (!surveyId) throw new Error("Missing surveyId");

      // What currently exists for this survey
      const { data: existingRows, error: existingErr } = await supabase
        .from("platform_survey_questions")
        .select("id")
        .eq("survey_id", surveyId);
      if (existingErr) throw existingErr;

      const existingIds = new Set((existingRows ?? []).map((r) => r.id));
      const keptIds = new Set(
        questions.map((q) => q.id).filter((qid): qid is string => !!qid && existingIds.has(qid)),
      );
      const idsToDelete = [...existingIds].filter((qid) => !keptIds.has(qid));

      // Guard: deleting a question cascades away its submitted answers.
      if (idsToDelete.length > 0 && !confirmAnswerLoss) {
        const { count: answerCount, error: answerErr } = await supabase
          .from("platform_survey_answers")
          .select("id", { count: "exact", head: true })
          .in("question_id", idsToDelete);
        if (answerErr) throw answerErr;

        if ((answerCount ?? 0) > 0) {
          const { count: responseCount } = await supabase
            .from("platform_survey_responses")
            .select("id", { count: "exact", head: true })
            .eq("survey_id", surveyId);
          throw new SurveyAnswerLossError(answerCount ?? 0, responseCount ?? 0, idsToDelete.length);
        }
      }

      // UPDATE the questions that already exist
      for (const [i, q] of questions.entries()) {
        const row = {
          question_text: q.question_text,
          question_type: q.question_type,
          options: q.options || null,
          impact_metric_key: q.impact_metric_key || null,
          // Auto-derive a stable grouping key from the question text when the
          // admin hasn't set one. Without this every question had a NULL key and
          // the cross-wave comparison view had nothing to bucket on.
          group_question_key:
            q.group_question_key || slugifyQuestionText(q.question_text) || null,
          display_order: i,
          required: q.required,
        };

        if (q.id && existingIds.has(q.id)) {
          const { data: updated, error: updErr } = await supabase
            .from("platform_survey_questions")
            .update(row)
            .eq("id", q.id)
            .eq("survey_id", surveyId)
            .select("id");
          if (updErr) throw updErr;
          if (!updated || updated.length === 0) {
            throw new Error(`Failed to update question ${q.id} — no rows changed.`);
          }
        } else {
          const { error: insErr } = await supabase
            .from("platform_survey_questions")
            .insert({ survey_id: surveyId, ...row })
            .select("id")
            .single();
          if (insErr) throw insErr;
        }
      }

      // DELETE only what the admin actually removed
      if (idsToDelete.length > 0) {
        const { error: delErr } = await supabase
          .from("platform_survey_questions")
          .delete()
          .in("id", idsToDelete)
          .eq("survey_id", surveyId);
        if (delErr) throw delErr;
      }

      const { data, error } = await supabase
        .from("platform_survey_questions")
        .select("*")
        .eq("survey_id", surveyId)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      if (surveyId) qc.invalidateQueries({ queryKey: ["platform_survey", surveyId] });
    },
  });
}

export function useSendPlatformSurveyWave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      surveyId: string;
      waveLabel: string;
      dueAt: string | null;
      targetUserIds: string[];
      cohortId: string | null;
    }) => {
      const { surveyId, waveLabel, dueAt, targetUserIds, cohortId } = input;
      if (!surveyId) throw new Error("Missing surveyId");
      if (!waveLabel?.trim()) throw new Error("Wave label is required");
      if (!targetUserIds || targetUserIds.length === 0) {
        throw new Error("No target users to assign");
      }

      // Guard: block dispatching draft surveys. Only `active` surveys may be sent.
      const { data: surveyRow, error: surveyErr } = await supabase
        .from("platform_surveys")
        .select("status")
        .eq("id", surveyId)
        .single();
      if (surveyErr) throw surveyErr;
      if (!surveyRow || surveyRow.status !== "active") {
        throw new Error(
          "This survey is not active. Activate the survey before sending a wave.",
        );
      }

      const rows = targetUserIds.map((uid) => ({
        survey_id: surveyId,
        user_id: uid,
        cohort_id: cohortId || null,
        wave_label: waveLabel.trim(),
        due_at: dueAt || null,
        notified_at: null,
      }));

      const { data: inserted, error: insErr } = await supabase
        .from("platform_survey_assignments")
        .insert(rows)
        .select("id");
      if (insErr) throw insErr;

      const ids = (inserted || []).map((r: any) => r.id);

      // Fire-and-forget: send email notifications. The edge function only
      // emails assignments where notified_at IS NULL and stamps it on success.
      if (ids.length > 0) {
        supabase.functions
          .invoke("survey-assignment-notify", { body: { assignmentIds: ids } })
          .then(({ error }) => {
            if (error) console.error("survey-assignment-notify failed:", error);
          })
          .catch((err) => console.error("survey-assignment-notify threw:", err));
      }

      return { count: ids.length };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
